/**
 * Filter resume data to remove empty sections
 * Only includes sections where user has actually filled data
 */

/**
 * Check if a string field has meaningful content
 */
const hasContent = (value) => {
  if (!value) return false;
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed !== '<p><br></p>' && trimmed !== '<p></p>';
};

/**
 * Check if personal info section has data
 */
const hasPersonalInfo = (data) => {
  if (!data) return false;
  
  // Check for common personal info field names
  return (
    hasContent(data.name) ||
    hasContent(data.fullName) ||
    hasContent(data.email) ||
    hasContent(data.phone) ||
    hasContent(data.location) ||
    hasContent(data.linkedin) ||
    hasContent(data.github) ||
    hasContent(data.portfolio) ||
    hasContent(data.website) ||
    hasContent(data.title) ||
    hasContent(data.summary)
  );
};

/**
 * Check if an experience item has data
 */
const hasExperienceData = (exp) => {
  if (!exp) return false;
  return (
    hasContent(exp.company) ||
    hasContent(exp.position) ||
    hasContent(exp.location) ||
    hasContent(exp.startDate) ||
    hasContent(exp.endDate) ||
    hasContent(exp.description) ||
    (Array.isArray(exp.responsibilities) && exp.responsibilities.some(r => hasContent(r)))
  );
};

/**
 * Check if an education item has data
 */
const hasEducationData = (edu) => {
  if (!edu) return false;
  return (
    hasContent(edu.school) ||
    hasContent(edu.degree) ||
    hasContent(edu.field) ||
    hasContent(edu.location) ||
    hasContent(edu.startDate) ||
    hasContent(edu.endDate) ||
    hasContent(edu.gpa) ||
    hasContent(edu.description)
  );
};

/**
 * Check if a project item has data
 */
const hasProjectData = (project) => {
  if (!project) return false;
  return (
    hasContent(project.name) ||
    hasContent(project.projectName) ||
    hasContent(project.description) ||
    hasContent(project.projectDescription) ||
    hasContent(project.technologies) ||
    hasContent(project.projectTechnologies) ||
    hasContent(project.link) ||
    hasContent(project.projectLink) ||
    hasContent(project.github) ||
    hasContent(project.startDate) ||
    hasContent(project.endDate)
  );
};

/**
 * Check if a certification item has data
 */
const hasCertificationData = (cert) => {
  if (!cert) return false;
  return (
    hasContent(cert.name) ||
    hasContent(cert.certificationName) ||
    hasContent(cert.issuer) ||
    hasContent(cert.issuingOrganization) ||
    hasContent(cert.certificationIssuer) ||
    hasContent(cert.date) ||
    hasContent(cert.issueDate) ||
    hasContent(cert.certificationDate) ||
    hasContent(cert.expiryDate) ||
    hasContent(cert.credentialId) ||
    hasContent(cert.url)
  );
};

/**
 * Check if skills section has data
 */
const hasSkillsData = (resumeData) => {
  if (!resumeData) return false;
  
  // Check for different skill field structures
  const skillFields = [
    'skills',
    'technicalSkills', 
    'softSkills',
    'languages',
    'tools'
  ];
  
  for (const field of skillFields) {
    const skillData = resumeData[field];
    
    // Check if it's an array with content
    if (Array.isArray(skillData) && skillData.length > 0) {
      const hasData = skillData.some(skill => {
        if (typeof skill === 'string') {
          return hasContent(skill);
        }
        if (typeof skill === 'object') {
          return hasContent(skill.category) || 
                 hasContent(skill.name) ||
                 hasContent(skill.items) ||
                 (Array.isArray(skill.skills) && skill.skills.some(s => hasContent(s)));
        }
        return false;
      });
      
      if (hasData) return true;
    }
    
    // Check if it's an object with categories
    if (skillData && typeof skillData === 'object' && !Array.isArray(skillData)) {
      const hasData = Object.values(skillData).some(value => hasContent(value));
      if (hasData) return true;
    }
  }
  
  return false;
};

/**
 * Filter resume data to only include sections with content
 */
export const filterResumeData = (resumeData) => {
  if (!resumeData) return null;

  const filtered = {};

  // Personal info fields - include if any field has data
  const personalInfoFields = ['fullName', 'name', 'email', 'phone', 'location', 
    'linkedin', 'github', 'portfolio', 'website', 'title', 'summary',
    'photoURL', 'photoDataUri'];
  
  const personalInfo = {};
  let hasAnyPersonalInfo = false;
  
  personalInfoFields.forEach(field => {
    if (resumeData[field] && hasContent(resumeData[field])) {
      personalInfo[field] = resumeData[field];
      hasAnyPersonalInfo = true;
    }
  });
  
  if (hasAnyPersonalInfo) {
    filtered.personalInfo = personalInfo;
    // Also copy direct fields to root level for template compatibility
    Object.assign(filtered, personalInfo);
  }

  // Filter experience section (check both 'experience' and 'experiences')
  const experienceField = resumeData.experiences || resumeData.experience;
  if (Array.isArray(experienceField) && experienceField.length > 0) {
    const filteredExperience = experienceField.filter(hasExperienceData);
    if (filteredExperience.length > 0) {
      filtered.experiences = filteredExperience;
      filtered.experience = filteredExperience; // Alias for compatibility
    }
  }

  // Filter education section
  if (Array.isArray(resumeData.education) && resumeData.education.length > 0) {
    const filteredEducation = resumeData.education.filter(hasEducationData);
    if (filteredEducation.length > 0) {
      filtered.education = filteredEducation;
    }
  }

  // Filter skills - handle multiple skill fields
  if (hasSkillsData(resumeData)) {
    // Copy all skill-related fields that have data
    ['skills', 'technicalSkills', 'softSkills', 'languages', 'tools'].forEach(field => {
      if (resumeData[field]) {
        if (Array.isArray(resumeData[field]) && resumeData[field].length > 0) {
          const filteredSkills = resumeData[field].filter(skill => {
            if (typeof skill === 'string') return hasContent(skill);
            if (typeof skill === 'object') return Object.values(skill).some(v => hasContent(v));
            return false;
          });
          if (filteredSkills.length > 0) {
            filtered[field] = filteredSkills;
          }
        } else if (typeof resumeData[field] === 'object') {
          filtered[field] = resumeData[field];
        }
      }
    });
  }

  // Filter projects section
  if (Array.isArray(resumeData.projects) && resumeData.projects.length > 0) {
    const filteredProjects = resumeData.projects.filter(hasProjectData);
    if (filteredProjects.length > 0) {
      filtered.projects = filteredProjects;
    }
  }

  // Filter certifications section
  if (Array.isArray(resumeData.certifications) && resumeData.certifications.length > 0) {
    const filteredCertifications = resumeData.certifications.filter(hasCertificationData);
    if (filteredCertifications.length > 0) {
      filtered.certifications = filteredCertifications;
    }
  }

  // Filter awards section
  if (Array.isArray(resumeData.awards) && resumeData.awards.length > 0) {
    const filteredAwards = resumeData.awards.filter(award => 
      hasContent(award.title) || hasContent(award.issuer) || hasContent(award.date)
    );
    if (filteredAwards.length > 0) {
      filtered.awards = filteredAwards;
    }
  }

  // Filter publications section
  if (Array.isArray(resumeData.publications) && resumeData.publications.length > 0) {
    const filteredPublications = resumeData.publications.filter(pub => 
      hasContent(pub.title) || hasContent(pub.publisher) || hasContent(pub.date)
    );
    if (filteredPublications.length > 0) {
      filtered.publications = filteredPublications;
    }
  }

  // Filter volunteer section
  if (Array.isArray(resumeData.volunteer) && resumeData.volunteer.length > 0) {
    const filteredVolunteer = resumeData.volunteer.filter(vol => 
      hasContent(vol.organization) || hasContent(vol.role) || hasContent(vol.description)
    );
    if (filteredVolunteer.length > 0) {
      filtered.volunteer = filteredVolunteer;
    }
  }

  return filtered;
};

/**
 * Get a summary of which sections have data
 */
export const getResumeDataSummary = (resumeData) => {
  const filtered = filterResumeData(resumeData);
  
  return {
    hasPersonalInfo: !!filtered.personalInfo,
    hasExperience: !!filtered.experience,
    hasEducation: !!filtered.education,
    hasSkills: !!filtered.skills,
    hasProjects: !!filtered.projects,
    hasCertifications: !!filtered.certifications,
    hasLanguages: !!filtered.languages,
    hasAwards: !!filtered.awards,
    hasPublications: !!filtered.publications,
    hasVolunteer: !!filtered.volunteer,
    totalSections: Object.keys(filtered).length
  };
};

/**
 * Validate if resume has minimum required data for export
 */
export const canExportResume = (resumeData) => {
  const summary = getResumeDataSummary(resumeData);
  
  // Minimum requirement: At least ONE section with data (can be any section)
  // Personal info is NOT required - export whatever the user filled
  return summary.totalSections >= 1;
};
